import React, { useState, useEffect, useMemo } from 'react';
import { useCart } from '../../context/CartContext';
import { textures as defaultTextures } from '../../data/products';
import { useAdmin } from '../../context/AdminContext';
import './ProductModal.css';

const getLuminance = (hex) => {
  const rgb = hex.replace('#', '');
  const r = parseInt(rgb.substr(0, 2), 16) / 255;
  const g = parseInt(rgb.substr(2, 2), 16) / 255;
  const b = parseInt(rgb.substr(4, 2), 16) / 255;

  const [rs, gs, bs] = [r, g, b].map(val => {
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastColor = (backgroundColor) => {
  const luminance = getLuminance(backgroundColor);
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

const ProductModal = ({ product, selectedTexture, isOpen, onClose, onAddToCart, onAddTextureSet }) => {
  const { addToCart: contextAddToCart, addTextureSetToCart } = useCart();
  const { textures: adminTextures } = useAdmin();
  const [shareCopied, setShareCopied] = useState(false);
  const [purchaseMode, setPurchaseMode] = useState('single');
  const [selectedBundleTextures, setSelectedBundleTextures] = useState([]);

  const textures = useMemo(() => {
    if (Array.isArray(adminTextures) && adminTextures.length > 0) {
      const texturesObj = {};
      adminTextures.forEach(texture => {
        texturesObj[texture.id] = { name: texture.name, color: texture.color };
      });
      return { ...defaultTextures, ...texturesObj };
    }
    return defaultTextures;
  }, [adminTextures]);

  const [currentTexture, setCurrentTexture] = useState(
    selectedTexture || (product?.textures && product.textures.length > 0 ? product.textures[0] : null)
  );
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (isOpen && product) {
      const texture = selectedTexture || (product.textures && product.textures.length > 0 ? product.textures[0] : null);
      setCurrentTexture(texture);
      setPurchaseMode('single');
      setSelectedBundleTextures([]);
      setImageLoaded(false);
    }
  }, [isOpen, product, selectedTexture]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!product || !isOpen) return null;

  const handleShare = () => {
    const url = `${window.location.origin}/shop/${product.id}`;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => {
        setShareCopied(true);
        setTimeout(() => setShareCopied(false), 2000);
      });
    } else {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  const currentImage = (currentTexture && product.images?.[currentTexture]) || product.images?.default || '';
  const basePrice = parseFloat(product.price) || 0;
  const discountedPrice = product.discountedPrice ?? product.discounted_price;
  const hasDiscount = discountedPrice !== null && discountedPrice !== undefined && discountedPrice !== '';
  const effectivePrice = hasDiscount ? (parseFloat(discountedPrice) || 0) : basePrice;
  const bundleQuantity = Math.max(2, Number.parseInt(product.bundleQuantity, 10) || 3);
  const bundleDiscountPercent = parseFloat(product.bundleDiscountPercent) || 0;
  const canBuildBundle = Boolean(product.bundleEnabled)
    && bundleDiscountPercent > 0
    && product.textures?.length >= bundleQuantity;
  const bundleUnitPrice = Math.round(effectivePrice * (1 - bundleDiscountPercent / 100) * 100) / 100;

  const toggleBundleTexture = (texture) => {
    setCurrentTexture(texture);
    setSelectedBundleTextures(current => {
      if (current.includes(texture)) {
        return current.filter(item => item !== texture);
      }
      if (current.length >= bundleQuantity) {
        return [...current.slice(1), texture];
      }
      return [...current, texture];
    });
  };

  return (
    <>
      <div
        className="product-modal-overlay"
        onClick={onClose}
      >
        <div
          className="product-modal-content"
          onClick={(e) => e.stopPropagation()}
        >
          <button className="product-modal-close" onClick={onClose}>
            ×
          </button>
          <button
            className={`product-modal-share-btn${shareCopied ? ' copied' : ''}`}
            onClick={handleShare}
            title={shareCopied ? 'Link copied!' : 'Share this product'}
          >
            {shareCopied ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            )}
            <span>{shareCopied ? 'Copied!' : 'Share'}</span>
          </button>

          <div className="product-modal-grid">
            <div className="product-modal-image-container">
              <img
                src={currentImage}
                alt={product.name}
                className="product-modal-image"
                onLoad={() => setImageLoaded(true)}
                style={{ opacity: imageLoaded ? 1 : 0 }}
              />
              {!imageLoaded && (
                <div className="product-modal-image-placeholder">Loading...</div>

              )}
            </div>
            <div className="product-modal-info">
              <div className="product-modal-header">
                {product.hidden === true && (
                  <span className="product-modal-badge hidden">
                    Hidden
                  </span>
                )}
                {product.featured === true && (
                  <span className="product-modal-badge featured">
                    Featured
                  </span>
                )}
                {product.newArrival === true && (
                  <span className="product-modal-badge new">
                    New
                  </span>
                )}
                {hasDiscount && (
                  <span className="product-modal-badge discount">
                    Discount
                  </span>
                )}
                <h2 className="product-modal-name">{product.name}</h2>
              </div>

              <div className="product-modal-price-wrap">
                {hasDiscount && <p className="product-modal-price-old">${basePrice.toFixed(2)}</p>}
                <p className="product-modal-price">${effectivePrice.toFixed(2)}</p>
              </div>
              {product.description && (
                <p className="product-modal-description">{product.description}</p>
              )}
              {product.additionalInfo && (
                <p className="product-modal-additional-info">{product.additionalInfo}</p>
              )}
              {canBuildBundle && (
                <div className="product-purchase-modes" role="group" aria-label="Purchase type">
                  <button
                    type="button"
                    className={purchaseMode === 'single' ? 'active' : ''}
                    onClick={() => setPurchaseMode('single')}
                  >
                    Single
                  </button>
                  <button
                    type="button"
                    className={purchaseMode === 'bundle' ? 'active' : ''}
                    onClick={() => setPurchaseMode('bundle')}
                  >
                    Color / Style Set
                  </button>
                </div>
              )}
              {purchaseMode === 'bundle' && canBuildBundle && (
                <div className="product-bundle-offer">
                  <strong>Choose {bundleQuantity} different Color / Style options</strong>
                  <span>{bundleDiscountPercent}% off each item · ${bundleUnitPrice.toFixed(2)} each</span>
                </div>
              )}
              {product.textures && product.textures.length > 0 && (
                <div className="product-modal-textures">
                  <label className="texture-label">
                    {purchaseMode === 'bundle' ? `Select Color / Style: ${selectedBundleTextures.length} / ${bundleQuantity}` : 'Select Color / Style: *'}
                  </label>
                  <div className="texture-options">
                    {product.textures.map((texture) => {
                      const textureData = textures[texture];
                      if (!textureData) return null;
                      const bgColor = textureData.color;
                      const textColor = getContrastColor(bgColor);
                      return (
                        <button
                          key={texture}
                          className={`texture-option ${(purchaseMode === 'bundle' ? selectedBundleTextures.includes(texture) : currentTexture === texture) ? 'active' : ''}`}
                          onClick={() => purchaseMode === 'bundle' ? toggleBundleTexture(texture) : setCurrentTexture(texture)}
                          style={{
                            backgroundColor: bgColor,
                            color: textColor
                          }}
                        >
                          <span className="texture-name" style={{ color: textColor }}>
                            {textureData.name}
                          </span>
                          {(purchaseMode === 'bundle' ? selectedBundleTextures.includes(texture) : currentTexture === texture) && (
                            <span className="texture-check" style={{ color: textColor }}>✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <button
                className="product-modal-add-to-cart"
                onClick={() => {
                  if (purchaseMode === 'bundle') {
                    if (selectedBundleTextures.length !== bundleQuantity) {
                      alert(`Please select ${bundleQuantity} different Color / Style options`);
                      return;
                    }
                    try {
                      if (onAddTextureSet) {
                        onAddTextureSet(product, selectedBundleTextures);
                      } else {
                        addTextureSetToCart(product, selectedBundleTextures);
                      }
                      onClose();
                    } catch (error) {
                      alert(error.message || 'Error adding Color / Style set to cart');
                    }
                    return;
                  }
                  if (!currentTexture && product.textures && product.textures.length > 0) {
                    alert('Please select a Color / Style');
                    return;
                  }
                  try {
                    const textureToAdd = product.textures && product.textures.length > 0 ? currentTexture : 'default';
                    if (onAddToCart) {
                      onAddToCart(product, textureToAdd);
                    } else {
                      contextAddToCart(product, textureToAdd);
                    }
                    onClose();
                  } catch (error) {
                    alert(error.message || 'Error adding to cart');
                  }
                }}
                disabled={purchaseMode === 'bundle'
                  ? selectedBundleTextures.length !== bundleQuantity
                  : product.textures && product.textures.length > 0 && !currentTexture}
              >
                {purchaseMode === 'bundle' ? `Add ${bundleQuantity}-Color / Style Set` : 'Add to Cart'}
              </button>

            </div>

          </div>

        </div>

      </div>

    </>

  );
};

export default ProductModal;

